import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { ProductFormModal } from '../pages/products/ProductFormModal';
import type { VariantType } from '../types/product';

type FormState = null | undefined | string;
// null = closed, undefined = create mode, string = edit mode (productId)

export interface ProductFormPrefill {
  title?: string;
  description?: string; // HTML or plain text
  price?: number;
  salePrice?: number;
  compareAtPrice?: number;
  sku?: string;
  quantity?: number;
  unlimitedQuantity?: boolean;
  productType?: 'physical' | 'digital' | 'service' | 'code' | 'file';
  accessibility?: 'public' | 'private' | 'password' | 'archived';
  weight?: number;
}

export interface ProductFormHandle {
  setFields: (values: ProductFormPrefill) => void;
  addVariantType: (input: { name: string; options: string[] }) => VariantType;
  addCategory: (categoryId: string) => void;
  /**
   * Patch a single auto-generated variant identified by its option-name combination.
   * Returns true if the variant was found and patched, false otherwise.
   */
  setVariantOverride: (input: {
    combination: Record<string, string>; // { typeName: optionName, e.g. {Color: "Red", Size: "L"} }
    price?: number;
    priceModifier?: number;
    quantity?: number;
    sku?: string;
    isActive?: boolean;
  }) => boolean;
}

interface ProductFormContextValue {
  openCreate: () => void;
  openEdit: (productId: string) => void;
  close: () => void;
  isOpen: boolean;
  editingProductId: string | null;
  formRef: MutableRefObject<ProductFormHandle | null>;
}

const ProductFormContext = createContext<ProductFormContextValue | null>(null);

export function ProductFormProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FormState>(null);
  const formRef = useRef<ProductFormHandle | null>(null);

  const openCreate = useCallback(() => setState(undefined), []);
  const openEdit = useCallback((productId: string) => setState(productId), []);
  const close = useCallback(() => setState(null), []);

  const value = useMemo<ProductFormContextValue>(
    () => ({
      openCreate,
      openEdit,
      close,
      isOpen: state !== null,
      editingProductId: typeof state === 'string' ? state : null,
      formRef,
    }),
    [state, openCreate, openEdit, close],
  );

  return (
    <ProductFormContext.Provider value={value}>
      {children}
      <ProductFormModal
        ref={formRef}
        isOpen={state !== null}
        onClose={close}
        productId={state ?? null}
      />
    </ProductFormContext.Provider>
  );
}

export function useProductForm() {
  const ctx = useContext(ProductFormContext);
  if (!ctx) throw new Error('useProductForm must be used inside ProductFormProvider');
  return ctx;
}
